import React from 'react';
import { Search, X } from 'lucide-react';

interface Filters {
  type: 'all' | 'meeting' | 'email' | 'system' | 'advisor';
  dateRange: 'all' | 'today' | 'week' | 'month' | 'quarter' | 'year';
  search: string;
}

interface NoteFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
}

const NoteFilters: React.FC<NoteFiltersProps> = ({ filters, onFilterChange }) => {
  // Filter type options
  const filterTypes: Array<{ value: Filters['type']; label: string }> = [
    { value: 'all', label: 'All Types' },
    { value: 'meeting', label: 'Meetings' },
    { value: 'email', label: 'Emails' },
    { value: 'system', label: 'System' },
    { value: 'advisor', label: 'Advisor' },
  ];

  // Date range options
  const dateRangeOptions: Array<{ value: Filters['dateRange']; label: string }> = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 days' },
    { value: 'month', label: 'Last 30 days' },
    { value: 'quarter', label: 'Last 3 months' },
    { value: 'year', label: 'Last year' },
  ];

  const handleTypeChange = (type: Filters['type']) => {
    onFilterChange({ ...filters, type });
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ ...filters, dateRange: e.target.value as Filters['dateRange'] });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleClearFilters = () => {
    onFilterChange({ type: 'all', dateRange: 'all', search: '' });
  };

  const hasActiveFilters = filters.type !== 'all' || filters.dateRange !== 'all' || filters.search !== '';

  return (
    <div className="pb-4 border-b border-gray-200">
      {/* Type filter buttons */}
      <div className="flex items-center gap-2 mb-3">
        {filterTypes.map((filterType) => (
          <button
            key={filterType.value}
            onClick={() => handleTypeChange(filterType.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filters.type === filterType.value
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filterType.label}
          </button>
        ))}
      </div>

      {/* Date range and search row */}
      <div className="flex items-center gap-3">
        {/* Date range dropdown */}
        <select
          value={filters.dateRange}
          onChange={handleDateRangeChange}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
        >
          {dateRangeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search notes..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            title="Clear filters"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Clear</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default NoteFilters;
export type { Filters };
